// frontend/src/pages/SearchResults.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader,
  Search as SearchIcon,
  Sparkles,
  Star,
  TrendingUp,
} from "lucide-react";

import { useAnime } from "../context/AnimeContext";
import AnimeCard from "../components/AnimeCard";
import { searchAnime } from "../services/api";

/* -----------------------------
   Helpers
------------------------------ */
function n(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : null;
}

function getAnimeKey(anime) {
  const rawId =
    anime?.AnimeID ??
    anime?.anime_id ??
    anime?.id ??
    anime?.AnimeId ??
    anime?.animeId;

  if (rawId !== undefined && rawId !== null) return String(rawId);

  const t = String(anime?.Title ?? anime?.title ?? "anime").trim();
  return `${t}-${Math.random().toString(36).slice(2)}`;
}

function resolveTitle(anime) {
  return String(anime?.Title ?? anime?.title ?? "").trim();
}

function resolveGenres(anime) {
  const g = anime?.Genres ?? anime?.genres;
  if (!g) return [];
  if (Array.isArray(g)) return g.map((x) => String(x || "").trim()).filter(Boolean);
  return String(g)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function resolveRating(anime) {
  return (
    n(anime?.AverageRating) ??
    n(anime?.average_rating) ??
    n(anime?.rating) ??
    n(anime?.AniListRating) ??
    n(anime?.anilist_rating) ??
    null
  );
}

function resolveId(anime) {
  const rawId =
    anime?.AnimeID ??
    anime?.anime_id ??
    anime?.id ??
    anime?.AnimeId ??
    anime?.animeId;
  return rawId != null ? String(rawId) : null;
}

function resolveImageSrc(anime) {
  const url =
    anime?.PhotoUrl ??
    anime?.photo_url ??
    anime?.image_url ??
    anime?.cover_image_url ??
    anime?.coverImageUrl ??
    anime?.banner_image_url ??
    anime?.bannerImageUrl;

  if (url && typeof url === "string" && url.trim() && !url.includes("example.com")) {
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/")) return url;
    return `/${url.replace(/^\/+/, "")}`;
  }

  const t = resolveTitle(anime).toLowerCase().replace(/[^a-z0-9]/gi, "").trim();
  return t ? `/images/${t}.jpg` : "/images/placeholder.jpg";
}

function pillTextFromGenres(genres, max = 4) {
  const list = (genres || []).slice(0, max);
  return list.length ? list.join(" • ") : "Explore • Discover • Rate";
}

/**
 * Supports both 0–5 and 0–10 scales:
 * - if rating <= 5 => stars = rating
 * - else stars = rating/2
 */
function toStars(rating) {
  if (rating == null) return null;
  const r = Number(rating);
  if (!Number.isFinite(r)) return null;
  return r <= 5 ? r : r / 2;
}

/* -----------------------------
   Mobile detection
------------------------------ */
function useIsMobile(maxWidthPx = 640) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`);

    const onChange = (e) => setIsMobile(e.matches);

    // set immediately (in case of hydration/resizes)
    setIsMobile(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [maxWidthPx]);

  return isMobile;
}

/**
 * Hard-coded demo ratings (used ONLY in the Top Rated sidebar).
 * Make sure the keys match your DB titles exactly.
 */
const HARD_CODED_TOP_RATINGS = {
  "Black Clover": 5.0,
  "Kimetsu no Yaiba": 4.8,
  "Shingeki no Kyojin": 4.9,
  "Shigatsu wa Kimi no Uso": 4.7,
  "Jujutsu Kaisen": 4.6,
  "Death Note": 4.8,
};

/* -----------------------------
   Component
------------------------------ */
export default function SearchResults() {
  const navigate = useNavigate();
  const { animeList } = useAnime();

  const isMobile = useIsMobile(640); // Tailwind "sm" breakpoint
  const cardVariant = isMobile ? "genre" : "trendingOverlay";

  const [query, setQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("All");
  const [sortOption, setSortOption] = useState("rating");

  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isSearching, setIsSearching] = useState(false);

  const resultsPerPage = 50;

  const genres = [
    "All",
    "Action",
    "Adventure",
    "Drama",
    "Fantasy",
    "Sports",
    "Romance",
    "Mystery",
    "Psychological",
    "Sci-Fi",
    "Magic",
    "Mecha",
    "Horror",
    "Comedy",
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedGenre, sortOption]);

  useEffect(() => {
    const q = query.trim();

    const run = async () => {
      if (q.length > 2) {
        await handleApiSearch(q);
      } else {
        await loadAllAnime();
      }
    };

    const t = setTimeout(run, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, currentPage, sortOption]);

  async function loadAllAnime() {
    try {
      setIsSearching(true);

      const API_BASE = process.env.REACT_APP_API_BASE || "/api";
      const sortParam =
        sortOption === "title" ? "title" : sortOption === "rating" ? "rating" : "anime_id";

      const res = await fetch(
        `${API_BASE}/anime?page=${currentPage}&limit=${resultsPerPage}&sort=${sortParam}`
      );
      const data = await res.json();

      setSearchResults(data?.results || []);
      setTotalResults(n(data?.pagination?.total) ?? 0);
      setTotalPages(n(data?.pagination?.lastPage) ?? 1);
    } catch (err) {
      console.error("Load error:", err);
      setSearchResults([]);
      setTotalResults(0);
      setTotalPages(1);
    } finally {
      setIsSearching(false);
    }
  }

  async function handleApiSearch(q) {
    try {
      setIsSearching(true);

      const data = await searchAnime(q, currentPage, resultsPerPage);

      const results = data?.results ?? data ?? [];
      const total = n(data?.pagination?.total) ?? n(data?.total) ?? results.length;
      const pages =
        n(data?.pagination?.lastPage) ?? Math.max(1, Math.ceil(total / resultsPerPage));

      setSearchResults(results);
      setTotalResults(total);
      setTotalPages(pages);
    } catch (err) {
      console.error("Search error:", err);
      setSearchResults([]);
      setTotalResults(0);
      setTotalPages(1);
    } finally {
      setIsSearching(false);
    }
  }

  const displayedAnime = useMemo(() => {
    let list = Array.isArray(searchResults) ? searchResults.slice() : [];

    if (selectedGenre !== "All") {
      const want = selectedGenre.toLowerCase();
      list = list.filter((a) =>
        resolveGenres(a).some((g) => String(g).toLowerCase() === want)
      );
    }

    list.sort((a, b) => {
      if (sortOption === "title") return resolveTitle(a).localeCompare(resolveTitle(b));
      return (resolveRating(b) ?? 0) - (resolveRating(a) ?? 0);
    });

    return list;
  }, [searchResults, selectedGenre, sortOption]);

  const featured = useMemo(() => {
    const base = displayedAnime.length ? displayedAnime : animeList || [];
    const sorted = base
      .slice()
      .filter((a) => resolveTitle(a))
      .sort((a, b) => (resolveRating(b) ?? 0) - (resolveRating(a) ?? 0));

    return sorted[0] ?? base[0] ?? null;
  }, [displayedAnime, animeList]);

  const topRated = useMemo(() => {
    const wanted = new Set(Object.keys(HARD_CODED_TOP_RATINGS));

    const matches = (animeList || [])
      .slice()
      .filter((a) => wanted.has(resolveTitle(a)))
      .map((a) => ({
        a,
        stars: HARD_CODED_TOP_RATINGS[resolveTitle(a)] ?? 4.5,
      }))
      .sort((x, y) => (y.stars ?? 0) - (x.stars ?? 0))
      .slice(0, 6)
      .map((x) => x.a);

    return matches;
  }, [animeList]);

  const getPageNumbers = () => {
    // fewer buttons on mobile so pagination doesn’t wrap ugly
    const maxVisible = isMobile ? 5 : 7;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) return [1, 2, 3, "…", totalPages];

    if (currentPage >= totalPages - 2) {
      const tail = [];
      for (let i = totalPages - 2; i <= totalPages; i++) tail.push(i);
      return [1, "…", ...tail];
    }

    return [1, "…", currentPage, "…", totalPages];
  };

  const handlePreviousPage = () => {
    if (currentPage <= 1) return;
    setCurrentPage((p) => p - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextPage = () => {
    if (currentPage >= totalPages) return;
    setCurrentPage((p) => p + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageClick = (p) => {
    if (!Number.isFinite(Number(p))) return;
    setCurrentPage(Number(p));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const featuredTitle = featured ? resolveTitle(featured) : "";
  const featuredGenres = featured ? resolveGenres(featured) : [];
  const featuredImage = featured ? resolveImageSrc(featured) : null;
  const featuredId = featured ? resolveId(featured) : null;

  return (
    <div className="min-h-screen bg-[#070b1a] text-white overflow-x-hidden">
      {/* No background gradients. Stars + subtle dark wash only. */}
      <div className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-0 opacity-[0.55]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.12) 1px, transparent 1px)",
            backgroundSize: "42px 42px",
          }}
        />
        <div className="absolute inset-0 bg-black/35" />
      </div>

      <div className="relative mx-auto max-w-[1600px] px-3 sm:px-4 md:px-6 pt-5 sm:pt-6 pb-14">
        {/* Back button ABOVE the "Discover" label, top-left, with breathing room */}
        <div className="mb-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl border border-white/12 bg-white/[0.05] backdrop-blur-xl px-3 py-2 text-xs text-white/80 hover:text-white hover:bg-white/[0.08] transition"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        </div>

        {/* Discover label row */}
        <div className="mb-4">
          <div className="text-[11px] tracking-[0.38em] uppercase text-white/55">
            Discover
          </div>

          {/* Search pill */}
          <div className="mt-3 flex items-center gap-3">
            <div className="relative flex-1">
              <SearchIcon
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45"
                size={18}
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="w-full h-12 rounded-full border border-white/12 bg-white/[0.04] backdrop-blur-xl pl-12 pr-24 sm:pr-28 text-sm text-white placeholder-white/45 focus:outline-none focus:border-white/20 focus:bg-white/[0.06] transition"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] tracking-[0.26em] uppercase text-white/45 hidden sm:block">
                Explore
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
          {/* MAIN */}
          <div className="min-w-0 space-y-6">
            {/* FEATURED / TRENDING hero panel */}
            <section className="rounded-[28px] border border-white/12 bg-white/[0.045] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.65)] overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-[420px_1fr]">
                <div className="p-5 sm:p-7 md:p-8">
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.34em] uppercase text-white/45">
                    <TrendingUp size={14} className="text-emerald-200/80" />
                    Trending
                  </div>

                  <h1 className="mt-3 text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
                    {featuredTitle || "Explore Anime"}
                  </h1>

                  <p className="mt-2 text-sm text-white/60">
                    {pillTextFromGenres(featuredGenres)}
                  </p>

                  <div className="mt-5 flex items-center gap-3 flex-wrap">
                    {featuredId ? (
                      <Link
                        to={`/anime/${featuredId}`}
                        className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] px-4 py-2 text-sm text-white/85 hover:bg-white/[0.08] hover:border-white/20 transition"
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/15 bg-black/30">
                          <Sparkles size={12} className="text-emerald-200/80" />
                        </span>
                        Details
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-white/45">
                        Details
                      </span>
                    )}

                    <div className="text-xs text-white/45">
                      {featured ? (
                        <>
                          Rating{" "}
                          <span className="text-emerald-200/80 font-semibold">
                            {toStars(resolveRating(featured))?.toFixed(1) ?? "—"} ★
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="relative min-h-[210px] sm:min-h-[260px] md:min-h-[320px]">
                  {featuredImage ? (
                    <img
                      src={featuredImage}
                      alt={featuredTitle}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : null}

                  {/* No gradients. Just soft dark overlays + side fades. */}
                  <div className="absolute inset-0 bg-black/35" />
                  <div className="absolute inset-0 opacity-90">
                    <div className="absolute inset-y-0 left-0 w-[18%] sm:w-[22%] bg-[#070b1a]" />
                    <div className="absolute inset-y-0 right-0 w-[18%] sm:w-[22%] bg-[#070b1a]" />
                  </div>
                </div>
              </div>
            </section>

            {/* FILTER BAR */}
            <section className="rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_70px_rgba(0,0,0,0.35)] p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] tracking-[0.34em] uppercase text-white/45">
                    Genre
                  </div>
                  <select
                    value={selectedGenre}
                    onChange={(e) => setSelectedGenre(e.target.value)}
                    className="h-10 flex-1 sm:flex-none rounded-full bg-black/25 border border-white/12 text-white/85 px-4 text-sm focus:outline-none focus:border-white/20 transition"
                  >
                    {genres.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-[10px] tracking-[0.34em] uppercase text-white/45">
                    Sort
                  </div>
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="h-10 flex-1 sm:flex-none rounded-full bg-black/25 border border-white/12 text-white/85 px-4 text-sm focus:outline-none focus:border-white/20 transition"
                  >
                    <option value="rating">Rating</option>
                    <option value="title">Title</option>
                  </select>
                </div>

                <div className="sm:ml-auto text-xs text-white/50">
                  {totalResults > 0 ? (
                    <>
                      Showing {(currentPage - 1) * resultsPerPage + 1}–
                      {Math.min(currentPage * resultsPerPage, totalResults)} of{" "}
                      {totalResults}
                    </>
                  ) : null}
                </div>
              </div>
            </section>

            {/* RESULTS */}
            <section className="rounded-[26px] border border-white/12 bg-white/[0.045] backdrop-blur-xl shadow-[0_25px_110px_rgba(0,0,0,0.55)] overflow-hidden">
              <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <SearchIcon size={16} className="text-emerald-200/80" />
                  <h2 className="text-base sm:text-lg font-semibold text-white">
                    {query ? "Search Results" : "All Anime"}
                  </h2>
                </div>

                <div className="text-[10px] tracking-[0.34em] uppercase text-white/45">
                  Browse
                </div>
              </div>

              <div className="p-4 sm:p-6">
                {isSearching ? (
                  <div className="flex flex-col items-center justify-center py-14">
                    <Loader className="animate-spin text-emerald-300 mb-4" size={34} />
                    <p className="text-white/60 text-sm">Searching…</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                      {displayedAnime.length > 0 ? (
                        displayedAnime.map((anime) => (
                          <AnimeCard
                            key={getAnimeKey(anime)}
                            anime={anime}
                            variant={cardVariant} // mobile uses "genre", desktop uses "trendingOverlay"
                          />
                        ))
                      ) : (
                        <div className="col-span-full text-center py-14">
                          <p className="text-white/60 text-base sm:text-lg px-4">
                            {selectedGenre !== "All"
                              ? `No anime found in ${selectedGenre}`
                              : query
                              ? `No results found for "${query}"`
                              : "No anime available"}
                          </p>
                        </div>
                      )}
                    </div>

                    {totalPages > 1 ? (
                      <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                        <div className="flex items-center justify-center flex-wrap gap-1.5">
                          <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className={[
                              "h-10 px-3 rounded-full border text-sm font-semibold transition",
                              currentPage === 1
                                ? "bg-black/25 border-white/8 text-white/30 cursor-not-allowed"
                                : "bg-black/25 border-white/12 text-white/85 hover:bg-white/[0.07] hover:border-white/20",
                            ].join(" ")}
                          >
                            <ChevronLeft size={16} />
                          </button>

                          {getPageNumbers().map((p, idx) =>
                            p === "…" ? (
                              <span key={`ellipsis-${idx}`} className="px-2 text-white/40">
                                …
                              </span>
                            ) : (
                              <button
                                key={p}
                                onClick={() => handlePageClick(p)}
                                className={[
                                  "h-10 min-w-[40px] px-3 rounded-full border text-sm font-semibold transition",
                                  currentPage === p
                                    ? "bg-emerald-500/18 border-emerald-300/30 text-emerald-100"
                                    : "bg-black/25 border-white/12 text-white/85 hover:bg-white/[0.07] hover:border-white/20",
                                ].join(" ")}
                              >
                                {p}
                              </button>
                            )
                          )}

                          <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className={[
                              "h-10 px-3 rounded-full border text-sm font-semibold transition",
                              currentPage === totalPages
                                ? "bg-black/25 border-white/8 text-white/30 cursor-not-allowed"
                                : "bg-black/25 border-white/12 text-white/85 hover:bg-white/[0.07] hover:border-white/20",
                            ].join(" ")}
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>

                        <div className="text-center text-xs text-white/50">
                          Page {currentPage} of {totalPages} • {totalResults} total
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </section>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-4 lg:space-y-4">
            <section className="rounded-[26px] border border-white/12 bg-white/[0.045] backdrop-blur-xl shadow-[0_25px_110px_rgba(0,0,0,0.55)] overflow-hidden">
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/12 bg-black/25">
                    <Star size={16} className="text-emerald-200/80" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Top Rated</div>
                    <div className="text-[10px] tracking-[0.34em] uppercase text-white/45">
                      Rating Radar
                    </div>
                  </div>
                </div>

                <div className="text-[10px] tracking-[0.34em] uppercase text-white/45">
                  4.5★+
                </div>
              </div>

              <div className="p-4">
                <div className="space-y-3">
                  {topRated.length > 0 ? (
                    topRated.slice(0, 6).map((anime, index) => {
                      const animeId = resolveId(anime);
                      const title = resolveTitle(anime);
                      const stars = HARD_CODED_TOP_RATINGS[title] ?? 4.5;
                      const imageSrc = resolveImageSrc(anime);

                      return (
                        <Link
                          key={`${getAnimeKey(anime)}-${index}`}
                          to={animeId ? `/anime/${animeId}` : "#"}
                          className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 hover:bg-white/[0.06] hover:border-white/18 transition"
                        >
                          <div className="flex items-center justify-center h-10 w-10 rounded-xl border border-white/10 bg-white/[0.03] text-white/80 font-semibold">
                            {index + 1}
                          </div>

                          <img
                            src={imageSrc}
                            alt={title}
                            className="h-12 w-12 rounded-xl object-cover border border-white/10"
                            loading="lazy"
                          />

                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-white/90 truncate">
                              {title || "Untitled"}
                            </div>

                            <div className="mt-1">
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2.5 py-1 text-[11px] text-emerald-100/90">
                                <Star size={12} className="text-emerald-200/80" />
                                {stars.toFixed(1)}
                              </span>
                            </div>
                          </div>

                          <div className="text-white/35 group-hover:text-white/55 transition">
                            <ChevronRight size={16} />
                          </div>
                        </Link>
                      );
                    })
                  ) : (
                    <div className="py-10 text-center text-white/55 text-sm">
                      None of your hard-coded titles were found in animeList.
                      <div className="mt-2 text-xs text-white/40">
                        (Check the titles match your DB exactly.)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
